import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FieldScreen from './FieldScreen';
import FieldGalleryList from './FieldGalleryList';
import { FieldModeSwitch } from './FieldModePill';
import { useFieldMode } from '../../utils/fieldMode';
import { can, isOutsider, isPublicOnly } from '../../utils/permissions';
import { pageStyles as ps } from './fieldFormat';
import useFieldSheet from './useFieldSheet';
import FieldTasksSheet from './FieldTasksSheet';
import { mayHaveTasks, useFieldTasks } from '../../utils/fieldTasks';

/**
 * The gallery as a page (/field/gallery): the list (FieldGalleryList.js)
 * under Field Mode's header, with the camera's mode switch and the map. Home's
 * isolates hub opens it; from the camera, the same list comes in as a sheet
 * (FieldCamera.js, ?sheet=gallery). Its Tasks button opens Field Mode's Tasks
 * sheet over it (?sheet=tasks, FieldTasksSheet.js; plan phase 6), as the
 * camera's chip does; Start in Field Mode there goes to the camera.
 */
export default function FieldGallery({ store, outbox, userId, user }) {
  const { t } = useTranslation('field');
  const navigate = useNavigate();
  const location = useLocation();
  const canCollect = can(user, 'specimen:register');
  const [fieldMode, setFieldMode] = useFieldMode(userId, canCollect);
  // Back to the camera: the entry it came from, where there is one.
  const back = () => (location.state?.fromCamera ? navigate(-1) : navigate('/field', { replace: true }));
  const { sheet, values, open, close } = useFieldSheet();
  const mayTasks = mayHaveTasks(user);
  const tasks = useFieldTasks(user, { enabled: mayTasks && outbox?.signedIn?.() !== false });

  return (
    <FieldScreen
      testid="field-gallery-screen"
      title={t('gallery.title')}
      backLabel={t('gallery.toCamera')}
      backTip={t('tip.gallery.toCamera')}
      onBack={back}
      // Pushed, like the camera's Map chip: Back returns to this gallery.
      actions={(
        <>
          {/* The camera's switch here too: what the next specimen starts as. */}
          <FieldModeSwitch mode={fieldMode} onChange={setFieldMode} canCollect={canCollect}
            testId="gallery-field-mode" />
          {/* An outsider's way to the Biobank catalog (public portal 2b); staff
              reach it from Bank capabilities. */}
          {isOutsider(user) && (
            <button type="button" style={ps.headerBtn} data-testid="screen-catalog" aria-label={t('gallery.catalog')}
              title={t('tip.gallery.catalog')} onClick={() => navigate('/catalog')}>
              <span aria-hidden="true">📚</span>
            </button>
          )}
          {!isPublicOnly(user) && (
            <button type="button" style={ps.headerBtn} data-testid="screen-map" aria-label={t('gallery.map')}
              title={t('tip.gallery.map')} onClick={() => navigate('/map', { state: { fromField: true } })}>
              <span aria-hidden="true">🗺</span>
            </button>
          )}
          {mayTasks && (
            <button type="button" style={ps.headerBtn} data-testid="screen-tasks"
              title={tasks.state === 'ready' ? t('tasks.waiting', { count: tasks.count }) : t('tasks.open')}
              aria-label={tasks.state === 'ready' ? t('tasks.chipCount', { count: tasks.count }) : t('tasks.chip')}
              aria-haspopup="dialog" onClick={() => open('tasks')}>
              <span aria-hidden="true">📋</span>
            </button>
          )}
        </>
      )}
    >
      <FieldGalleryList store={store} outbox={outbox} userId={userId} user={user} />
      <FieldTasksSheet open={mayTasks && sheet === 'tasks'} onClose={close} user={user} tasks={tasks}
        focusMission={values.mission} onStart={() => navigate('/field', { replace: true })} />
    </FieldScreen>
  );
}
